import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/ku';
import i18n from '@/i18n';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

export function formatDate(value?: string | null, fallback = '—'): string {
  return value ? dayjs(value).format('YYYY-MM-DD') : fallback;
}

export function formatDateTime(value?: string | null, fallback = '—'): string {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : fallback;
}

/**
 * The clock time alone. For "try again at 14:32" — a refusal that names a
 * moment rather than a wait, because a wait in seconds has to be counted down
 * by the reader and is wrong as soon as it is printed.
 */
export function formatTime(value?: string | null, fallback = '—'): string {
  return value ? dayjs(value).format('HH:mm') : fallback;
}

/**
 * Rounding steps for "how long ago", dayjs's own: under 45 seconds is
 * seconds, under 45 minutes is minutes, and so on. Each row is the largest
 * span, in seconds, that is still counted in that unit.
 */
const RELATIVE_STEPS: [Intl.RelativeTimeFormatUnit, number, number][] = [
  // unit, seconds per unit, counted in this unit while under
  ['second', 1, 45],
  ['minute', 60, 45 * 60],
  ['hour', 3600, 22 * 3600],
  ['day', 86400, 26 * 86400],
  ['month', 30 * 86400, 320 * 86400],
  ['year', 365 * 86400, Infinity],
];

/**
 * "9 minutes ago" in the reader's language. The browser's
 * `Intl.RelativeTimeFormat`, not dayjs: dayjs's Arabic has one plural form,
 * so it wrote "منذ 2 أيام" and "منذ 11 دقائق" where Arabic says "قبل يومين"
 * and "قبل 11 دقيقة". Latin digits (`nu-latn`), as every other figure in the
 * product. Sorani has no data in the browsers, so a locale Intl cannot serve
 * falls back to dayjs's `ku` rather than to English.
 */
export function formatRelative(value?: string | null, fallback = '—'): string {
  if (!value) return fallback;
  return formatOffset(dayjs(value).diff(dayjs(), 'second'));
}

/**
 * "5 minutes ago" from an age the **server** measured, in seconds. For
 * tracking, where the API hands over `ageSeconds` / `lastHeardAgeSeconds`
 * against the tracking server's clock at `generatedAt`: the browser's clock is
 * never consulted, so a laptop that is ten minutes out cannot age every van.
 * Same rounding and the same language rules as `formatRelative`.
 */
export function formatAgo(seconds?: number | null, fallback = '—'): string {
  if (seconds == null || !Number.isFinite(seconds)) return fallback;
  // A device clock a little ahead of the server's reads as "now", never "in 3 seconds".
  return formatOffset(-Math.max(0, seconds));
}

/** Signed seconds from now (negative is the past) as a phrase. */
function formatOffset(seconds: number): string {
  const language = i18n.language?.split('-')[0] || 'ar';
  const locale = `${language}-u-nu-latn`;
  if (Intl.RelativeTimeFormat.supportedLocalesOf(locale).length === 0) {
    // dayjs only phrases a span between two instants; both ends are built from
    // the same reference, so the result is the span and nothing else.
    const reference = dayjs(0);
    return reference
      .add(seconds, 'second')
      .locale(language === 'ckb' ? 'ku' : 'en')
      .from(reference);
  }
  const [unit, size] = RELATIVE_STEPS.find(([, , under]) => Math.abs(seconds) < under)!;
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.round(seconds / size), unit);
}

export function initials(name?: string | null): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Renders a bilingual record using the active locale, falling back to the other. */
export function localized(locale: string, ar?: string | null, en?: string | null): string {
  const preferArabic = locale.startsWith('ar') || locale.startsWith('ckb');
  return (preferArabic ? ar || en : en || ar) ?? '—';
}

export function toCsv(rows: Record<string, unknown>[], columns: { field: string; header: string }[]): string {
  const escape = (value: unknown) => {
    const text = value == null ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const header = columns.map((c) => escape(c.header)).join(',');
  const body = rows.map((row) => columns.map((c) => escape(row[c.field])).join(',')).join('\n');
  return `${header}\n${body}`;
}

export function downloadCsv(filename: string, csv: string) {
  // A BOM keeps Excel from mangling Arabic when it opens the file.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
